'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { RadarMark } from '@/components/brand/RadarMark'
import { VectorMark, HiveMark } from '@/components/brand/product-marks'
import './brand-system-case-study.css'

/**
 * Pixelmojo brand system case study.
 *
 * A direct port of `public/radar-branding/pixelmojo-brand-family.html`. That
 * file is the design contract: markup, class names, copy, and section order
 * match it one-for-one so the two can be diffed. Styling lives in the sibling
 * `brand-system-case-study.css`, scoped under `.brand-cs`.
 *
 * This renders full-bleed — `WorkPage`'s hero and container are skipped for
 * this slug (see `fullBleedProjects` in ./index.ts) because the case study
 * ships its own hero.
 *
 * Three deliberate breaks from the source:
 *   1. The marks come from `RadarMark` / `VectorMark` / `HiveMark`, which are
 *      the only sanctioned way to render Radar and product branding. The
 *      source redrew all three in CSS divs, and that reconstruction rendered
 *      the Radar quarter-disc mirrored and Vector's counter-triangle in the
 *      wrong corner. Everything else about the marks — placement, sizing,
 *      the on-dark neutrals, the hero spectrum fill — is unchanged.
 *   2. Its own sticky header (brand word, section nav, CTA) and skip link are
 *      dropped. The site header already sticks at the top of every page, and
 *      two sticky bars would fight. The section ids it linked to stay.
 *   3. Its `<main id="main" tabindex="-1">` is dropped because the root layout
 *      already renders `<main id="main-content">`.
 *
 * The inline script it carried is reproduced below: the product switcher, the
 * reveal-on-scroll observer, and the `enhanced` flag (now `is-enhanced` on the
 * wrapper instead of a class on <html>).
 */

const PRODUCTS = {
  radar: {
    verb: 'Radar sweeps.',
    description: 'Sees what AI gets wrong.',
    label:
      'Radar mark. The active pink quarter circle sweeps and the white counter dot records a finding.',
  },
  vector: {
    verb: 'Vector points.',
    description: 'Shows the move.',
    label:
      'Vector mark. The active pink triangle points toward the move and the white counter triangle marks the origin.',
  },
  hive: {
    verb: 'Hive multiplies.',
    description: 'The colony works as one.',
    label:
      'Hive mark. The active pink cell subdivides into a working colony and the white counter cell represents the individual.',
  },
} as const

type ProductName = keyof typeof PRODUCTS

const PRODUCT_ORDER: ProductName[] = ['radar', 'vector', 'hive']

const TAB_LABELS: Record<ProductName, string> = {
  radar: 'Radar',
  vector: 'Vector',
  hive: 'Hive',
}

const MARK_COMPONENTS = {
  radar: RadarMark,
  vector: VectorMark,
  hive: HiveMark,
} as const

/**
 * The canonical mark for a product, wearing the source's own class hooks so
 * the sheet can size it (`.mark`), target the hero spectrum (`.stage-mark`),
 * and switch on product (`.radar` / `.vector` / `.hive`). The components emit
 * their 64x64 viewBox with the 4-unit margin and gutter already in it, so
 * `.mark` only has to supply a box and a `color` for their `currentColor`
 * neutrals. Omitting `label` renders the mark `aria-hidden`, which is what the
 * source's decorative instances want.
 */
function Mark({
  product,
  className,
  label,
}: {
  product: ProductName
  className?: string
  label?: string
}) {
  const Component = MARK_COMPONENTS[product]
  return (
    <Component
      className={`mark ${product}${className ? ` ${className}` : ''}`}
      label={label}
    />
  )
}

export default function BrandSystemProjectContent() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [product, setProduct] = useState<ProductName>('radar')

  // The source sets `enhanced` on <html> at the end of its script, which is
  // what reveals the stage tabs. Without JS the stage stays on Radar and every
  // section is visible, exactly as the no-JS source renders.
  useEffect(() => setEnhanced(true), [])

  // Reveal-on-scroll.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const targets = root.querySelectorAll<HTMLElement>('[data-reveal]')
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('is-visible'))
      return
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
            currentObserver.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -7%' }
    )

    targets.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const active = PRODUCTS[product]

  return (
    <div ref={rootRef} className={`brand-cs${enhanced ? ' is-enhanced' : ''}`}>
      <div className='site-shell'>
        <section className='hero' id='top' aria-labelledby='hero-title'>
          <div className='hero-copy' data-reveal>
            <p className='section-label'>
              Brand system case study · V2 · Public
            </p>
            <h1 id='hero-title'>
              One grid.
              <br />
              Three verbs.
              <br />
              <span>Room to grow.</span>
            </h1>
            <p className='lede'>
              The identity system for the Pixelmojo product family. Radar
              sweeps. Vector points. Hive multiplies. One geometric grammar
              makes every product unmistakably itself and unmistakably ours.
            </p>
            <div className='button-row'>
              <a className='button button-primary' href='#system'>
                Explore the system
              </a>
              {/* Was `#build`, the removed proof section. It now goes straight
                  to the service that section was pointing at, so the hero keeps
                  both buttons live instead of one scrolling to nothing. */}
              <Link
                className='button'
                href='/services/revenue-first-design'
                data-cta-id='brand_system_build'
              >
                Build yours with us
              </Link>
            </div>
            <div className='hero-meta' aria-label='Document metadata'>
              <span>Identity · Product · Voice</span>
              <span>Pixelmojo · July 2026</span>
            </div>
          </div>

          <div
            className='identity-stage'
            data-product={product}
            data-reveal
            aria-label='Interactive Pixelmojo product mark explorer'
          >
            <div
              className='stage-tabs'
              role='group'
              aria-label='Choose a product mark'
            >
              {PRODUCT_ORDER.map(name => (
                <button
                  key={name}
                  className='stage-tab'
                  type='button'
                  data-product-button={name}
                  aria-pressed={product === name}
                  onClick={() => setProduct(name)}
                >
                  {TAB_LABELS[name]}
                </button>
              ))}
            </div>

            <div className='stage-visual'>
              <span className='signal vector-signal' aria-hidden='true' />
              <span className='signal hive-signal' aria-hidden='true' />

              <Mark
                product={product}
                className='stage-mark'
                label={active.label}
              />
            </div>

            <div className='stage-caption' aria-live='polite'>
              <div>
                <strong className='stage-verb'>{active.verb}</strong>
                <span className='stage-description'>{active.description}</span>
              </div>
              <span className='stage-measure'>
                64 × 64 master grid
                <br />
                Pink marks the action
              </span>
            </div>
          </div>
        </section>

        <section id='system' aria-labelledby='system-title'>
          <div className='manifesto-grid'>
            <div className='manifesto-copy' data-reveal>
              <p className='section-label'>01 · Why a system</p>
              <h2 id='system-title'>
                A logo describes one product. A grammar describes a company.
              </h2>
              <p>
                Pixelmojo ships instruments. Radar audits how AI reads your
                brand and hands you the fix. Vector shows the move your growth
                data is making. Hive multiplies the work of one team across many
                channels.
              </p>
              <p>
                Each product needed its own mark. A drawer of unrelated logos
                would fracture the brand with every launch. So the identity is
                not a logo. It is a rule for making logos. Customers learn the
                language once, then read every new mark as part of the same
                family.
              </p>
            </div>

            <aside className='pull-quote' data-reveal>
              <p>
                One geometry generates today’s three marks and whatever ships
                next.
              </p>
              <span className='micro-label'>
                Order · One action · One result
              </span>
            </aside>
          </div>

          <div
            className='assembly'
            aria-label='How the Pixelmojo mark grammar assembles'
            data-reveal
          >
            <div className='assembly-step'>
              <span className='assembly-number'>01</span>
              <div className='grammar-sample' aria-hidden='true'>
                <span className='quiet-on' />
                <span />
                <span />
                <span className='quiet-on' />
              </div>
              <strong className='assembly-title'>Two quiet cells</strong>
              <span className='assembly-note'>
                The baseline. The system working as expected.
              </span>
            </div>

            <div className='assembly-step'>
              <span className='assembly-number'>02</span>
              <div className='grammar-sample' aria-hidden='true'>
                <span />
                <span className='verb-on' />
                <span />
                <span />
              </div>
              <strong className='assembly-title'>One pink verb</strong>
              <span className='assembly-note'>
                The action. The thing this product does.
              </span>
            </div>

            <div className='assembly-step'>
              <span className='assembly-number'>03</span>
              <div className='grammar-sample' aria-hidden='true'>
                <span />
                <span />
                <span className='counter-on' />
                <span />
              </div>
              <strong className='assembly-title'>One counter-shape</strong>
              <span className='assembly-note'>
                The answer. The result, origin, or individual.
              </span>
            </div>

            <div className='assembly-step'>
              <span className='assembly-number'>04</span>
              <div className='grammar-sample' aria-hidden='true'>
                <span className='quiet-on' />
                <span className='verb-on' />
                <span className='counter-on' />
                <span className='quiet-on' />
              </div>
              <strong className='assembly-title'>The mark assembled</strong>
              <span className='assembly-note'>
                A clean monogram first. A product in motion second.
              </span>
            </div>
          </div>
        </section>

        <section id='family' aria-labelledby='family-title'>
          <div className='family-intro' data-reveal>
            <div>
              <p className='section-label'>02 · The family</p>
              <h2 id='family-title'>Three products. One bloodline.</h2>
            </div>
            <p>
              Every mark gets two reads. First, a calm geometric monogram. Then
              the product in motion: an audit mid-sweep, a trajectory underway,
              a colony at work.
            </p>
          </div>

          <div className='product-family'>
            <article className='product-card' data-reveal>
              <span className='product-verb'>Sweeps</span>
              <Mark product='radar' label='Radar product mark' />
              <h3>Radar</h3>
              <p>
                <strong>Sees what AI gets wrong.</strong> Thirteen AI visibility
                tools in one audit. Radar checks how ChatGPT, Claude, Gemini,
                and Perplexity read your brand, then hands you the exact fix for
                every finding.
              </p>
              <ul className='product-facts' aria-label='Radar facts'>
                <li>13 checks</li>
                <li>Live now</li>
                <li>Free to start</li>
              </ul>
              <Link className='product-link' href='/platform'>
                Run Radar
              </Link>
            </article>

            <article className='product-card' data-reveal>
              <span className='product-verb'>Points</span>
              <Mark product='vector' label='Vector product mark' />
              <h3>Vector</h3>
              <p>
                <strong>Shows the move.</strong> A twelve-dimension lead
                qualification engine configured to your ideal customer profile.
                It scores every inbound signal and routes only ready-to-buy
                leads to sales.
              </p>
              <ul className='product-facts' aria-label='Vector facts'>
                <li>12 dimensions</li>
                <li>60-day build</li>
                <li>You own the code</li>
              </ul>
              <Link className='product-link' href='/vector'>
                See Vector
              </Link>
            </article>

            <article className='product-card' data-reveal>
              <span className='product-verb'>Multiplies</span>
              <Mark product='hive' label='Hive product mark' />
              <h3>Hive</h3>
              <p>
                <strong>The colony works as one.</strong> Multi-agent AI
                co-workers coordinate autonomously, share intelligence, and run
                operations like a real team. Not more disconnected chatbots.
              </p>
              <ul className='product-facts' aria-label='Hive facts'>
                <li>Shared context</li>
                <li>12-week build</li>
                <li>You own the code</li>
              </ul>
              <Link className='product-link' href='/hive'>
                Meet Hive
              </Link>
            </article>
          </div>

          <aside className='sibling-test' data-reveal>
            <p className='section-label'>The sibling test</p>
            <p>
              Put any future mark beside these three. If someone can tell it
              belongs before reading the name, the verb is right. If the
              relationship needs explaining, the system has been broken.
            </p>
          </aside>
        </section>

        <section id='rules' aria-labelledby='rules-title'>
          <div className='construction-grid'>
            <div
              className='blueprint'
              data-reveal
              aria-label='Radar mark shown on the 64-unit construction grid'
            >
              <span className='measure-tag measure-top'>Canvas · 64 × 64</span>
              <span className='measure-tag measure-bottom'>Cell · 26 × 26</span>
              <div className='blueprint-frame'>
                <Mark product='radar' label='Radar construction mark' />
              </div>
            </div>

            <div className='construction-copy' data-reveal>
              <p className='section-label'>03 · Construction</p>
              <h2 id='rules-title'>Nothing arbitrary.</h2>
              <p>
                All three marks share one skeleton: a 64-unit canvas, 4-unit
                margin, 4-unit gutter, and four 26-unit cells. Every coordinate
                follows the brand’s 4-pixel spacing base.
              </p>
              <p>
                The verbs are derived, not drawn. Radar uses a true
                quarter-circle. Vector cuts the cell on its diagonal. Hive
                reapplies the master grid inside one cell so the system becomes
                fractal with itself.
              </p>

              <div
                className='spec-list'
                aria-label='Construction specifications'
              >
                <div className='spec-row'>
                  <span className='spec-key'>Canvas</span>
                  <span className='spec-value'>64 × 64 units</span>
                </div>
                <div className='spec-row'>
                  <span className='spec-key'>Margin / gutter</span>
                  <span className='spec-value'>4 units / 4 units</span>
                </div>
                <div className='spec-row'>
                  <span className='spec-key'>Radar</span>
                  <span className='spec-value'>R26 quarter arc + R8 blip</span>
                </div>
                <div className='spec-row'>
                  <span className='spec-key'>Vector</span>
                  <span className='spec-value'>Corner triangles · 26 + 16</span>
                </div>
                <div className='spec-row'>
                  <span className='spec-key'>Hive</span>
                  <span className='spec-value'>
                    11-unit subcells · 4-unit gutter
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className='rule-strip' aria-label='Core usage rules' data-reveal>
            <div className='rule'>
              <span className='rule-index'>01 · Space</span>
              <strong>Give it one cell.</strong>
              <span>Clearspace equals one cell width on every side.</span>
            </div>
            <div className='rule'>
              <span className='rule-index'>02 · Size</span>
              <strong>Keep it readable.</strong>
              <span>
                Use the optical small cut below 24 pixels. Never go below 16.
              </span>
            </div>
            <div className='rule'>
              <span className='rule-index'>03 · Color</span>
              <strong>Pink is the verb.</strong>
              <span>
                Nothing else gets it. Color marks the part doing the work.
              </span>
            </div>
            <div className='rule'>
              <span className='rule-index'>04 · Form</span>
              <strong>Protect the grammar.</strong>
              <span>
                No rotation, rounding, shadows, recoloring, repositioning, or
                gradient outside Radar.
              </span>
            </div>
          </div>
        </section>

        <section aria-labelledby='expression-title'>
          <div className='expression-head' data-reveal>
            <div>
              <p className='section-label'>04 · Expression</p>
              <h2 id='expression-title'>
                Color means something. Type has a job.
              </h2>
            </div>
            <p>
              Restraint creates recognition. One accent owns the action. Three
              type voices divide speaking, explaining, and measuring. No other
              typefaces enter the system.
            </p>
          </div>

          <div className='expression-grid' data-reveal>
            <div className='specimen'>
              <p className='micro-label'>Color · Semantic, never decorative</p>
              <div className='color-statement'>
                <span>Pink is the verb.</span>
                <br />
                Nothing else gets it.
              </div>
              <div className='swatches' aria-label='Pixelmojo core colors'>
                <div className='swatch-chip'>
                  <span className='swatch-color pink' />
                  <code>#F90B8A · Verb</code>
                </div>
                <div className='swatch-chip'>
                  <span className='swatch-color white' />
                  <code>#F5F6F7 · On dark</code>
                </div>
                <div className='swatch-chip'>
                  <span className='swatch-color black' />
                  <code>#0E0F12 · On light</code>
                </div>
              </div>
              <div className='spectrum' aria-hidden='true' />
              <div className='spectrum-note'>
                A-to-F grade spectrum · Radar hero only
              </div>
            </div>

            <div className='specimen'>
              <p className='micro-label'>Typography · Three fixed roles</p>
              <div className='type-sample'>
                <span className='type-display'>Grade the signal.</span>
                <span className='type-role'>Display · Space Grotesk 600</span>
              </div>
              <div className='type-sample'>
                <span className='type-body'>
                  You read the grade, you know what to do next. Momentum you can
                  see, week over week.
                </span>
                <span className='type-role'>Body · Geist Sans 400</span>
              </div>
              <div className='type-sample'>
                <span className='type-mono'>By Pixelmojo · 13 checks · A+</span>
                <span className='type-role'>
                  Instrument labels · Geist Mono 400 · 0.18em
                </span>
              </div>
            </div>

            <div className='specimen lockup-specimen'>
              {PRODUCT_ORDER.map(name => (
                <div className='lockup-demo' key={name}>
                  <div className='lockup'>
                    <Mark product={name} />
                    <span className='lockup-copy'>
                      <span className='lockup-name'>{TAB_LABELS[name]}</span>
                      <span className='lockup-signature'>By Pixelmojo</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby='applications-title'>
          <div className='application-head' data-reveal>
            <div>
              <p className='section-label'>05 · Applications</p>
              <h2 id='applications-title'>Every layout is the logo.</h2>
            </div>
            <p>
              The master quadrant becomes the layout grid. Two quiet cells. One
              active cell. One proof point. The pink cell always holds the
              message doing the work.
            </p>
          </div>

          <div className='application-grid'>
            <figure className='application-card' data-reveal>
              <div
                className='app-layout'
                role='img'
                aria-label='Audit grade layout. One pink top-right cell carries the A-plus grade. A white counter dot marks the finding in the bottom-left cell.'
              >
                <div className='app-cell' />
                <div className='app-cell active'>
                  <span className='app-grade'>A+</span>
                </div>
                <div className='app-cell counter' />
                <div className='app-cell' />
              </div>
              <figcaption>
                <span>Audit grade</span>
                <span>Radar</span>
              </figcaption>
            </figure>

            <figure className='application-card' data-reveal>
              <div
                className='app-layout'
                role='img'
                aria-label='Weekly signal layout. The bottom-left cell is the single pink action. A white counter dot marks proof in the bottom-right cell.'
              >
                <div className='app-cell'>
                  <span className='app-signal'>Weekly signal</span>
                </div>
                <div className='app-cell' />
                <div className='app-cell active' />
                <div className='app-cell counter' />
              </div>
              <figcaption>
                <span>Weekly pulse</span>
                <span>Product</span>
              </figcaption>
            </figure>

            <figure className='application-card' data-reveal>
              <div
                className='app-layout'
                role='img'
                aria-label='Campaign layout. The single pink top-left cell carries the message Grade the signal. A white counter dot marks proof in the bottom-right cell.'
              >
                <div className='app-cell active'>
                  <span className='app-message'>Grade the signal.</span>
                </div>
                <div className='app-cell' />
                <div className='app-cell' />
                <div className='app-cell counter' />
              </div>
              <figcaption>
                <span>Campaign</span>
                <span>GTM</span>
              </figcaption>
            </figure>
          </div>
          <p className='application-rule' data-reveal>
            One pink cell per surface. Never two.
          </p>
        </section>

        <section id='voice' aria-labelledby='voice-title'>
          <div className='voice-grid'>
            <div data-reveal>
              <p className='section-label'>06 · Voice</p>
              <h2 id='voice-title'>Speak like an instrument.</h2>
              <p>
                The family talks the way a good readout does: plain, direct,
                second person. Short declarative sentences. Verb-first calls to
                action of four words or fewer. Numbers only when they carry a
                real signal. No emoji, no hype, no dashes.
              </p>

              <div className='voice-principles' aria-label='Voice principles'>
                <div className='voice-principle'>
                  <span className='micro-label'>01</span>
                  <strong>Plain</strong>
                </div>
                <div className='voice-principle'>
                  <span className='micro-label'>02</span>
                  <strong>Direct</strong>
                </div>
                <div className='voice-principle'>
                  <span className='micro-label'>03</span>
                  <strong>Provable</strong>
                </div>
              </div>
            </div>

            <div className='voice-examples'>
              <div className='voice-panel good' data-reveal>
                <h3>Sounds like us</h3>
                <blockquote>“Radar sees what AI gets wrong.”</blockquote>
                <blockquote>
                  “You read the grade, you know the move.”
                </blockquote>
                <blockquote>
                  “Don’t trust an AI score because a tool made it. Trust it
                  because we can prove how it was made.”
                </blockquote>
              </div>
              <div className='voice-panel bad' data-reveal>
                <h3>Does not</h3>
                <blockquote>
                  “Revolutionize your AI-powered brand journey!”
                </blockquote>
                <blockquote>
                  “We’re passionate about supercharging synergies.”
                </blockquote>
                <blockquote>
                  “Anything with an exclamation mark and a rocket.”
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        <footer className='site-footer'>
          <p className='footer-statement'>
            Radar sweeps. Vector points. Hive multiplies.
          </p>
          <div className='footer-meta'>
            One grid · Three verbs
            <br />
            Pixelmojo · Makati, Philippines
          </div>
        </footer>
      </div>
    </div>
  )
}
